/**
 * Tema yonetimi.
 *
 * ── UC DURUM, IKI GORUNUM ────────────────────────────────────────────────────
 * Tercih uc olabilir: 'dark' | 'light' | 'system'. Uygulanan gorunum ikidir.
 * 'system' seciliyken isletim sistemi temasi degisirse arayuz ANINDA takip
 * eder — kullanici sayfayi yenilemek zorunda kalmaz.
 *
 * ── NEDEN HEM SUNUCUDA HEM localStorage'DA ───────────────────────────────────
 * localStorage: sayfa acilirken React yuklenmeden once uygulanmali, yoksa beyaz
 * parlama olur (bkz. index.html'deki inline script).
 * Sunucu: kullanici masaustu Cortex'e gectiginde ayni temayi gormeli.
 * Ikisi celisirse SUNUCU kazanir — o, hesabin gercegi.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api';
import type { ThemePreference } from '@shared/index';

const STORAGE_KEY = 'cortex.theme';

type Appearance = 'dark' | 'light';

interface ThemeContextValue {
  preference: ThemePreference;
  appearance: Appearance;
  setPreference: (value: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemAppearance(): Appearance {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function resolve(preference: ThemePreference): Appearance {
  return preference === 'system' ? systemAppearance() : preference;
}

function readStored(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'dark' || v === 'light' || v === 'system') return v;
  } catch {
    // Gizli sekmede localStorage erisimi hata verebilir; varsayilana duseriz.
  }
  return 'system';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStored);
  const [appearance, setAppearance] = useState<Appearance>(() => resolve(readStored()));

  // Isletim sistemi temasi degisirse 'system' tercihinde ANINDA takip et.
  useEffect(() => {
    if (preference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => setAppearance(systemAppearance());
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  useEffect(() => {
    setAppearance(resolve(preference));
  }, [preference]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', appearance);
    // Tarayici kendi arayuzunu (kaydirma cubugu, form denetimleri) uyumlasın.
    document.documentElement.style.colorScheme = appearance;
  }, [appearance]);

  const setPreference = useCallback((value: ThemePreference) => {
    setPreferenceState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* gizli sekme */
    }
    // Sunucuya yazmak KRITIK DEGIL: basarisiz olursa tema yine calisir,
    // yalnizca diger cihaza tasinmaz. Bu yuzden hata kullaniciya gosterilmez.
    void api.patch('/account/preferences', { theme: value }).catch(() => {});
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, appearance, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme yalnızca ThemeProvider içinde kullanılabilir.');
  return ctx;
}

/** Sunucudan gelen tercihi yerel duruma uygular (giris sonrasi). */
export function applyServerTheme(preference: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    /* gizli sekme */
  }
}
